
import { Event }     from './event';
import { EventType } from './event.type';
import {empty} from "@/util/util";

/**
 *
 * @class KeyboardEvent
 *
 * details about key events
 *
 * - - - - - - - - - - - - - - - - - - -
 * @author    Michael Garvin (mike.garvin@ironroad.ca)
 * @date      June 2022
 *
 */
export class KeyboardEvent extends Event {

  /** the key code for this event */
  private _code: number = 0;

  /** the ascii/logical name of this key */
  private _key: string = '';

  /** was the key pressed or released? (true == released) */
  private _up: boolean = false;

  /** was the alt key pressed at the same time? */
  private _altKey: boolean = false;

  /** was the shift key pressed at the same time? */
  private _shiftKey: boolean = false;

  /** was the ctrl key pressed at the same time? */
  private _ctrlKey: boolean = false;

  /** standard constructor */
  constructor(krpano: any, code : number, up: boolean , alt : boolean, shift : boolean, ctrl : boolean) {

    super(krpano, up ? EventType.KEY_UP : EventType.KEY_DOWN);

    this._code     = code;
    this._up       = up;
    this._altKey   = alt;
    this._shiftKey = shift;
    this._ctrlKey  = ctrl;
    this._key      = '';

    /*
     * other keys get special logical names
     *
     *   ref: https://css-tricks.com/snippets/javascript/javascript-keycodes/
     *   ref: https://developer.mozilla.org/en-US/docs/Web/API/UI_Events/Keyboard_event_key_values
     *
     */

    switch (this._code) {
      case 8:   this._key = 'Backspace';  break;
      case 9:   this._key = 'Tab';        break;
      case 13:  this._key = 'Enter';      break;
      case 16:  this._key = 'Shift';      break;
      case 17:  this._key = 'Control';    break;
      case 18:  this._key = 'Alt';        break;
      case 19:  this._key = 'Pause';      break;
      case 27:  this._key = 'Escape';     break;
      case 32:  this._key = 'Space';      break;
      case 33:  this._key = 'PageUp';     break;
      case 34:  this._key = 'PageDown';   break;
      case 35:  this._key = 'End';        break;
      case 36:  this._key = 'Home';       break;
      case 37:  this._key = 'ArowLeft';   break;
      case 38:  this._key = 'ArrowUp';    break;
      case 39:  this._key = 'ArrowRight'; break;
      case 40:  this._key = 'ArrowDown';  break;
      case 45:  this._key = 'Insert';     break;
      case 46:  this._key = 'Delete';     break;
      case 59:
      case 186: this._key = ';';         break;
      case 61:
      case 187: this._key = '=';          break;
      case 112: this._key = 'F1';         break;
      case 113: this._key = 'F2';         break;
      case 114: this._key = 'F3';         break;
      case 115: this._key = 'F4';         break;
      case 116: this._key = 'F5';         break;
      case 117: this._key = 'F6';         break;
      case 118: this._key = 'F7';         break;
      case 119: this._key = 'F8';         break;
      case 120: this._key = 'F9';         break;
      case 121: this._key = 'F10';        break;
      case 122: this._key = 'F11';        break;
      case 123: this._key = 'F12';        break;
      case 173:
      case 189: this._key = '-';          break;
      case 188: this._key = ',';          break;
      case 190: this._key = '.';          break;
      case 192: this._key = '`';          break;
      case 219: this._key = '[';          break;
      case 221: this._key = ']';          break;
    }

    if (this._key == '=' && this._shiftKey) {

      this._key = '+';
    }

    if (this._key == '-' && this._shiftKey) {

      this._key = '_';
    }

    if (this._key == '`' && this._shiftKey) {

      this._key = '~';
    }

    if (this._key == ',' && this._shiftKey) {

      this._key = '<';
    }

    if (this._key == '.' && this._shiftKey) {

      this._key = '>';
    }

    if (this._key == '[' && this._shiftKey) {

      this._key = '{';
    }

    if (this._key == ']' && this._shiftKey) {

      this._key = '}';
    }

    if (empty(this._key)) {

      /* map the key code to a name */

      if (this._code > 32 && this._code < 126) {

        this._key = String.fromCharCode(this._code);

        return ;
      }
    }
  }

  /** fetch the character code */
  code(): number {

    return this._code;
  }

  /** fetch the character logical name */
  key(): string {

    return this._key;
  }

  /** was the key pressed, or released? */
  up(): boolean {

    return this._up;
  }

  /** was SHIFT key down? */
  shift(): boolean {

    return this._shiftKey;
  }

  /** was SHIFT key down? */
  alt(): boolean {

    return this._altKey;
  }

  /** was SHIFT key down? */
  ctrl(): boolean {

    return this._ctrlKey;
  }
}
